import { apiClient } from '~/adapters/apiClient';
import log from '~/logger';
// Type guard helpers for safe transformation
const isString = (value) => typeof value === 'string';
const isNumber = (value) => typeof value === 'number';
const isStringArray = (value) => Array.isArray(value) && value.every((item) => typeof item === 'string');
const isProductArray = (value) => Array.isArray(value) &&
    value.every((item) => typeof item === 'object' &&
        item !== null &&
        'productId' in item &&
        'productCode' in item);
const getRequiredString = (value) => isString(value) ? value : '';
const getRequiredStringArray = (value) => isStringArray(value) ? value : [];
/**
 * Safely transforms raw model data from BFF into CpsModel.
 * Handles field name variations between list and single endpoints:
 * - lastModified / modificationDate -> lastModified
 * - lastModifiedBy / modifiedBy -> lastModifiedBy
 * - active (boolean or string) -> active (string)
 */
export const safeTransformModel = (modelData) => {
    if (!isNumber(modelData.id)) {
        throw new Error(`Invalid modelId: expected number, got ${typeof modelData.id}`);
    }
    if (!isString(modelData.modelName)) {
        throw new Error(`Invalid modelName: expected string, got ${typeof modelData.modelName}`);
    }
    // Handle field name variations between list and single endpoints
    const lastModified = getRequiredString(modelData.lastModified) ||
        getRequiredString(modelData.modificationDate);
    const lastModifiedBy = getRequiredString(modelData.lastModifiedBy) ||
        getRequiredString(modelData.modifiedBy);
    // Handle active as boolean or string
    const activeValue = modelData.active;
    const active = isString(activeValue) ? activeValue : String(activeValue ?? '');
    return {
        modelId: modelData.id,
        modelName: modelData.modelName,
        age: getRequiredString(modelData.age),
        active,
        construction: getRequiredString(modelData.construction),
        division: getRequiredString(modelData.division),
        family: getRequiredString(modelData.family),
        fitPreference: getRequiredString(modelData.fitPreference),
        franchise: getRequiredString(modelData.franchise),
        gender: getRequiredString(modelData.gender),
        midsoleDescription: getRequiredString(modelData.midsoleDescription),
        modelGroups: getRequiredStringArray(modelData.modelGroups),
        outsoleDescription: getRequiredString(modelData.outsoleDescription),
        platforms: getRequiredStringArray(modelData.platforms),
        plate: getRequiredString(modelData.plate),
        rise: getRequiredString(modelData.rise),
        silhouette: getRequiredString(modelData.silhouette),
        silhouetteType: getRequiredString(modelData.silhouetteType),
        sportActivity: getRequiredString(modelData.sportActivity),
        status: getRequiredString(modelData.status),
        subBrand: getRequiredString(modelData.subBrand),
        subFamily: getRequiredString(modelData.subFamily),
        upperDescription: getRequiredString(modelData.upperDescription),
        lastModified,
        lastModifiedBy,
        products: isProductArray(modelData.products) ? modelData.products : []
    };
};
export const fetchModelsData = async (filterInfo) => {
    try {
        return await apiClient.fetchResourceWithFilter({
            endpoint: '/product_planning/models/v1',
            filters: filterInfo,
            resourceName: 'models data',
            api: 'pulse-bff'
        });
    }
    catch (error) {
        log.error('Failed to fetch models data', {
            error: error instanceof Error ? error.message : 'Unknown error',
            filters: filterInfo.filters
        });
        return { pages: { totalPages: 0, totalResources: 0 }, objects: [] };
    }
};
export const fetchModelById = async (modelId) => {
    try {
        const response = await apiClient.get(`/product_planning/models/v1/${modelId}`, 'pulse-bff', undefined, 'model data');
        return safeTransformModel(response);
    }
    catch (error) {
        log.error('Failed to fetch model by id', {
            error: error instanceof Error ? error.message : 'Unknown error',
            modelId
        });
        return null;
    }
};
